import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ComponentRef,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { filter, map, takeUntil, withLatestFrom } from "rxjs/operators";
import {
  Account,
  AccountSelectorComponent,
  AccountType,
} from "../account-selector/account-selector.component";
import { ReviewState } from "src/app/review/models/review-state";
import { SignupService } from "../service/signup.service";
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { FacadeService } from "src/app/shared/service-facade/facade.service";
import { ProductEligibility } from "skyhopper-api";
import { SecureApplicationReasonEnum } from "src/app/shared/model/secure-application-reason.enum";
import { ReviewService } from "src/app/review/services/review.service";
import { ComponentNameEnum } from "src/app/shared/model/component-name.enum";
import { BrandType } from "../models/brand-type";
import { SettingsEnum } from "src/app/shared/model/settings.enum";

@Component({
  selector: "app-account-selection",
  templateUrl: "./account-selection.component.html",
  styleUrls: ["./account-selection.component.css"],
})
export class AccountSelectionComponent
  implements OnInit, AfterContentInit, AfterViewInit
{
  @Input() isInReview = false;
  @ViewChild("accountSelection", { read: ViewContainerRef })
  accountSelection!: ViewContainerRef;

  private _accountSelectorMap: Map<AccountSelectorComponent, boolean> =
    new Map();
  private _accountIsBeingEdited$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  selectableAccountTypes: AccountType[] = [];

  canShowTitle$ = this._accountIsBeingEdited$.pipe(
    filter(() => !!this.accountSelection),
    map(
      (isEditing) =>
        (this.accountSelection.length === 1 && !isEditing) ||
        this.accountSelection.length > 1
    )
  );

  canShowErrorMessage$ = this._accountIsBeingEdited$.pipe(
    map((isEditing) => !isEditing && this.hasError)
  );

  destroySubject: Subject<void> = new Subject();

  selectedAccounts: FormGroup = new FormGroup({
    accounts: new FormArray([], this._atLeastOne),
  });

  private _canEditAccount = true;
  public isAccountSelectionOpen = false;
  canAddAccount$ = this._accountIsBeingEdited$.pipe(
    withLatestFrom(this._signupService.canAddMultipleAccounts$),
    map(([inEdit, canAddMultipleAccount]) => {
      if (!canAddMultipleAccount) {
        return false;
      }

      if (this._accountSelectorMap.size >= 5) {
        return false;
      }

      if (this.isInReview && !inEdit) {
        return !this._signupService.accountSelection?.value.accounts.length;
      }

      return !inEdit;
    })
  );

  private _canDelete = true;
  canDelete$ = this._signupService.canAddMultipleAccounts$
    .pipe(takeUntil(this.destroySubject))
    .subscribe((value) => (this._canDelete = value));

  canShowMultiproductMessage$ = this._facade.secureApplicationReason$.pipe(
    withLatestFrom(this._facade.isPWABrand$),
    filter(
      ([secureApplicationReason, isPWA]) =>
        secureApplicationReason !== SecureApplicationReasonEnum.authenticated &&
        secureApplicationReason !==
          SecureApplicationReasonEnum.jointMaintenance &&
        secureApplicationReason !== SecureApplicationReasonEnum.pwa &&
        !isPWA
    ),
    map(() => true)
  );

  canEdit$ = this._accountIsBeingEdited$
    .pipe(
      withLatestFrom(
        this._facade.secureApplicationReason$,
        this._facade.isPWABrand$
      ),
      filter(
        ([selectorActive, secureApplicationReason, isPWA]) =>
          secureApplicationReason !==
            SecureApplicationReasonEnum.authenticated &&
          secureApplicationReason !==
            SecureApplicationReasonEnum.jointMaintenance &&
          secureApplicationReason !== SecureApplicationReasonEnum.pwa &&
          !isPWA
      ),
      map(([selectorActive]) => selectorActive),
      takeUntil(this.destroySubject)
    )
    .subscribe((selectorActive) => {
      if (Number(this._facade.getBrandId()) === BrandType.PrivateWealth) {
        return;
      }

      if (selectorActive) {
        this._disableEditOnExistingSelectors();
      } else {
        this._enableEditOnExistingSelectors();
      }
    });

  hasError = false;
  private _isAuthFlow = false;
  private _hasAuthFlowFinishedInitializing = false;

  constructor(
    private _reviewService: ReviewService,
    private _signupService: SignupService,
    private _facade: FacadeService
  ) {
    this._facade.selectableAccountTypes$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((value) => {
        this.selectableAccountTypes = value;
        Array.from(this._accountSelectorMap.keys()).forEach((selector) => {
          selector.setAccountTypes(value);

          const account = this._getAccount(
            selector.selectedAccount$.getValue()!.value
          );
          if (account) {
            const { observations, APYText } = account;
            selector.accountObservations = observations;
            selector.accountAPYText = APYText;
          }
        });
      });

    this._isAuthFlow = this._facade.isInAuthenticatedFlow$.getValue()[0];

    this._signupService.canAddMultipleAccounts$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((value) => {
        this._canEditAccount = value;
      });

    this._facade.isJointApplication$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((value) => {
        Array.from(this._accountSelectorMap.keys()).forEach(
          (selector) => (selector.isJoint = value)
        );
      });
  }

  ngOnInit(): void {
    if (!this.isInReview || this._isAuthFlow) {
      this._signupService.addAccountSelectionForm(this.selectedAccounts);
      this._setEditState();
    }

    if (this.isInReview) {
      this._signupService.accountSelection.statusChanges
        .pipe(takeUntil(this.destroySubject))
        .subscribe((status) => {
          if (status === "VALID") {
            this.hasError = false;
            this._accountIsBeingEdited$.next(
              this._accountIsBeingEdited$.getValue()
            );
          }

          this._assignErrorState();
        });

      this._facade.initializeAccountSelectionValidation();
    }
    this._accountIsBeingEdited$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((value) => (this.isAccountSelectionOpen = value));
  }

  ngAfterContentInit(): void {
    this._accountIsBeingEdited$.next(false);
  }

  ngAfterViewInit(): void {
    if (this.isInReview) {
      if (this._isAuthFlow) {
        this.addAccountSelector(this._facade.getStorageItem("productUid"));
        (
          Array.from(
            this._accountSelectorMap.keys()
          )[0] as AccountSelectorComponent
        ).add();
        this._hasAuthFlowFinishedInitializing = true;

        return;
      }

      this._facade.resetAccountState();
      this._initializeSelectionState();
    }
  }

  private _setEditState(): void {
    this._reviewService.editStateArray.push([
      ComponentNameEnum.AccountSelection.toString(),
      0,
    ]);
    this._reviewService.editStateMapChanged.emit();
  }

  private _removeEditState(): void {
    this._reviewService.editStateArray.splice(
      this._reviewService.editStateArray.findIndex(
        (val) =>
          val[0] === ComponentNameEnum.AccountSelection.toString() &&
          val[1] === 0,
        1
      )
    );
    this._reviewService.editStateMapChanged.emit();
  }

  private _initializeSelectionState(): void {
    this.selectedAccounts = this._signupService.accountSelection;

    const accounts = this.selectedAccounts.get("accounts") as FormArray;

    for (let i = 0; i < accounts.length; i++) {
      const accountFormGroup = accounts.at(i) as FormGroup;

      this.addAccountSelector(accountFormGroup.value.account, accountFormGroup);
    }
  }

  addAccountSelector(accountId?: string, accountForm?: FormGroup): void {
    // Dinamically create an account selector and setup event hooks
    const accountSelector = this.accountSelection.createComponent(
      AccountSelectorComponent
    );
    const instance = accountSelector.instance;
    this.isAccountSelectionOpen = true;
    this._accountSelectorMap.set(instance, true);

    instance.accountTypes = this.selectableAccountTypes;
    instance.accountEnabled = this._canEditAccount;
    instance.typeEnabled = this._canEditAccount;
    instance.canEdit = this._canEditAccount;
    instance.canDelete = this._canDelete;
    instance.isJoint = this._facade.isJointApplication();
    instance.accountRestrictionsText = this._facade.getStorageItem(
      SettingsEnum.AccountRestrictionsText
    );
    instance.ratesFeesLink = this._facade.getStorageItem(
      SettingsEnum.RatesFeesLink
    );

    // This event fires when account type has been selected
    instance.selectedAccountType$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((value) => {
        instance.accountListLabel = "Account";
      });

    // This event fires when the account has been added
    instance.addedAccount$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((account) => {
        this._removeEditState();

        if (this.isInReview) {
          this._updateProductSelection();

          return;
        }

        this._facade.accountAdded(account!);
      });

    // This event fires after editing a prevous account selection
    instance.editedAccount$
      .pipe(takeUntil(this.destroySubject))
      .subscribe(async ({ previousAccount, newAccount }) => {
        this._facade.accountEdited(previousAccount, newAccount);
        this._removeEditState();

        if (this.isInReview) {
          this._updateProductSelection();
        }
      });

    // This observable fires when the account is selected
    instance.selectedAccount$
      .pipe(
        takeUntil(this.destroySubject),
        filter((account) => !!account)
      )
      .subscribe(async (account) => {
        this._facade.setCanHaveJointAccount(
          this._isSelectedAccountsAllowJointAccount()
        );
        await this._facade.getAndSetProductSettings(account!.value);
      });

    // This event fires when initial account selection has been canceled
    instance.cancelAccountSelection$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((selector) => {
        // Remove state and dynamic component;
        this._removeEditState();
        this._removeFormFromArray(instance.accountForm);
        this._destroySelectorComponentRefInstance(accountSelector, selector);
      });

    instance.cancelEdit$.pipe(takeUntil(this.destroySubject)).subscribe(() => {
      this._removeEditState();
    });

    // This event fires when the account selector review state changes
    instance.reviewState$
      .pipe(
        takeUntil(this.destroySubject),
        filter((value) => !!value)
      )
      .subscribe((state) => {
        if (state !== ReviewState.ReadOnly) {
          this._setEditState();
        }

        this._accountIsBeingEdited$.next(state !== ReviewState.ReadOnly);
      });

    // This event fires when manually hitting the delete button on the account selector
    instance.deleteAccountSelection$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((selector) => {
        // Remove state and dynamic component;
        this._removeEditState();
        this._facade.accountDeleted(selector.selectedAccount$.getValue()!);
        this._removeFormFromArray(instance.accountForm);
        this._destroySelectorComponentRefInstance(accountSelector, selector);
        this._facade.setCanHaveJointAccount(
          this._isSelectedAccountsAllowJointAccount()
        );

        if (this.isInReview) {
          this._updateProductSelection();
        }
      });

    // This is to trigger initialization events on dynamic component.
    accountSelector.changeDetectorRef.detectChanges();

    instance.reviewState = this._getInitialReviewState(
      this.isInReview,
      accountForm
    );

    if (this.isInReview && instance.reviewState !== ReviewState.ReadOnly) {
      this._accountIsBeingEdited$.next(true);
    }

    if (!accountForm) {
      (this.selectedAccounts.get("accounts") as FormArray).push(
        instance.accountForm
      );
    }

    if (accountForm) {
      instance.reviewState = ReviewState.ReadOnly;
      instance.accountForm = accountForm;
      instance.initializeFormStreams();
    }

    if (accountId) {
      instance.setSelectedAccount(accountId);
    }

    if (accountForm) {
      instance.setReadOnly();
      this._facade.accountAdded(this._getAccount(accountForm.value.account)!);
    }
  }

  private _isSelectedAccountsAllowJointAccount(): boolean {
    return !!!Array.from(this._accountSelectorMap.keys()).find(
      (selector) =>
        !selector.selectedAccount$.getValue()?.allowJointApplications
    );
  }

  private _getInitialReviewState(
    isInReview: boolean,
    accountForm?: FormGroup
  ): ReviewState {
    if (isInReview) {
      if (accountForm) {
        return ReviewState.ReadOnly;
      } else {
        return ReviewState.Initial;
      }
    } else {
      return ReviewState.Initial;
    }
  }

  private async _updateProductSelection() {
    if (!this._isAuthFlow || this._hasAuthFlowFinishedInitializing) {
      const { status } = this._facade.getShoppingCartApplicationLogsRequest();
      await this._facade.addProductSelectionToApplication(status);
    }

    this._facade.updateBSAProductProperties();
    this._facade.accountSelectionHasBeenUpdated();

    if (this.accountSelection.length) {
      this._facade.getCurrentProductSelectionEligibility();
      return;
    }

    this._facade.initializeAccountSelectionValidation();
  }

  private _removeFormFromArray(formGroup: FormGroup): void {
    const formArray = this.selectedAccounts.get("accounts") as FormArray;
    let formIndex: number;

    for (let i = 0; i < formArray.length; i++) {
      const selectedFormGroup = formArray.at(i);

      if (selectedFormGroup.value.account === formGroup.value.account) {
        formIndex = i;
        break;
      }
    }

    formArray.removeAt(formIndex!);
    this.selectedAccounts.updateValueAndValidity();
  }

  private _destroySelectorComponentRefInstance(
    selectorComponentRef: ComponentRef<AccountSelectorComponent>,
    selectorInstance: AccountSelectorComponent
  ): void {
    this._accountSelectorMap.delete(selectorInstance);
    selectorComponentRef.destroy();

    this._accountIsBeingEdited$.next(false);
  }

  private _disableEditOnExistingSelectors(): void {
    Array.from(this._accountSelectorMap.keys()).forEach(
      (selector) => (selector.canEdit = false)
    );
  }

  private _enableEditOnExistingSelectors(): void {
    Array.from(this._accountSelectorMap.keys()).forEach(
      (selector) => (selector.canEdit = true)
    );
  }

  private _assignErrorState(): void {
    Array.from(this._accountSelectorMap.keys()).forEach(
      (selector) => (selector.error = "")
    );

    if (!this.selectedAccounts.errors) {
      return;
    }

    const productErrors: Array<ProductEligibility> =
      this.selectedAccounts.errors!["productErrors"];

    productErrors.forEach((error) => {
      if (!error.message) {
        return;
      }

      const errorSelector = Array.from(this._accountSelectorMap.keys()).find(
        (selector) =>
          error.productCode === selector.accountForm.value["accountCode"]
      );

      if (!errorSelector) {
        return;
      }

      errorSelector.error = error.message;
    });

    this.hasError = true;
    this._accountIsBeingEdited$.next(this._accountIsBeingEdited$.getValue());
  }

  private _getAccount(productUID: string): Account | undefined {
    const type = this.selectableAccountTypes.find((type) =>
      type.accounts.find(({ value }) => value === productUID)
    );

    if (!type) {
      return undefined;
    }

    return type!.accounts.find(({ value }) => value === productUID)!;
  }

  private _atLeastOne(control: AbstractControl): ValidationErrors | null {
    const invalid = (control as FormArray).length === 0;
    return invalid ? { atLeastOne: true } : null;
  }
}
